import { FeatureState, isFeatureEnabled } from '@etrigan/feature-toggles-client'
import { Features } from '@news-mono/web-common'

interface BauhausArticle {
    id: string
    feature: Features
}

export function isBauhausArticle(
    topic: string,
    toggles: FeatureState,
): boolean {
    const bauhausArticles: BauhausArticle[] = [
        {
            id: 'entertainment/big-brother-australia',
            feature: '7ent-article-layout',
        },
        {
            id: 'lifestyle',
            feature: '7-news-lifestyle-article-full-page-display',
        },
        {
            id: 'travel',
            feature: '7-news-lifestyle-article-full-page-display',
        },
        {
            id: 'entertainment/dancing-with-the-stars',
            feature: '7news-dwts-full-article-page-display',
        },
        {
            id: 'entertainment/sas-australia',
            feature: '7-news-sas-australia-article-full-page-display',
        },
        {
            id: 'sport',
            feature: '7-news-sport-article-full-page-display',
        },
        {
            id: 'politics',
            feature: '7-news-politics-full-page-article-display',
        },
        {
            id: 'entertainment',
            feature: '7-entertainment-article-full-page-display',
        },
        {
            id: 'sunrise',
            feature: '7-entertainment-sunrise-full-article-display',
        },
        {
            id: 'the-morning-show',
            feature: '7-entertainment-the-morning-show-full-article-display',
        },
        {
            id: 'business',
            feature: '7-news-finance-business-full-article-display',
        },
        {
            id: 'technology',
            feature: '7-news-tech-full-article-page-display',
        },
        {
            id: 'news/world',
            feature: '7-news-world-full-article-page-display',
        },
        { id: 'news', feature: '7-news-article-full-page-display' },
        {
            id: 'weather',
            feature: '7-news-weather-full-article-page-display',
        },
    ]

    const isBauhausArticle = bauhausArticles.find((item) =>
        topic.includes(item.id),
    )
    if (!isBauhausArticle) {
        return false
    }

    const isBauhausArticleFeatureEnabled = isFeatureEnabled(
        toggles,
        isBauhausArticle.feature,
    )
    return isBauhausArticleFeatureEnabled
}

export function isBauhausSidebar(
    toggles: FeatureState,
    pathName?: string,
): boolean {
    const bauhausArticles: BauhausArticle[] = [
        {
            id: 'lifestyle',
            feature: '7-news-lifestyle-right-hand-side-the-latest',
        },
        {
            id: 'travel',
            feature: '7-news-lifestyle-right-hand-side-the-latest',
        },
        {
            id: 'entertainment',
            feature: 'entertainment-trending',
        },
        {
            id: 'sport',
            feature: '7-news-sport-article-full-page-display',
        },
        {
            id: 'politics',
            feature: '7-news-politics-full-page-article-display',
        },
        {
            id: 'sunrise',
            feature: '7-entertainment-sunrise-full-article-display',
        },
        {
            id: 'the-morning-show',
            feature: '7-entertainment-the-morning-show-full-article-display',
        },
        {
            id: 'technology',
            feature: '7-news-tech-full-article-page-display',
        },
        {
            id: 'news',
            feature: '7-news-article-full-page-display',
        },
        {
            id: 'world',
            feature: '7-news-world-full-article-page-display',
        },
        {
            id: 'business',
            feature: '7-news-finance-business-full-article-display',
        },
        {
            id: 'weather',
            feature: '7-news-weather-full-article-page-display',
        },
    ]
    if (!pathName) {
        return false
    }
    const isBauhausArticle = bauhausArticles.find((item) =>
        pathName.includes(item.id),
    )
    if (!isBauhausArticle) {
        return false
    }
    const isBauhausArticleFeatureEnabled = isFeatureEnabled(
        toggles,
        isBauhausArticle.feature,
    )
    return isBauhausArticleFeatureEnabled
}

export interface SevenPlusButtonArticles {
    id: string
    feature?: string
    url: string
}

export function getSevenPlusArticleButtonLink(
    topic: string,
    toggles: FeatureState,
): { isEnabled: boolean; buttonLink?: string } {
    const includedArticles: SevenPlusButtonArticles[] = [
        {
            id: 'entertainment/sas-australia',
            url: 'https://7plus.com.au/sas-australia?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=sas-australia',
        },
        {
            id: 'entertainment/dancing-with-the-stars',
            url: 'https://7plus.com.au/dancing-with-the-stars-all-stars?utm_source=7NEWS&utm_medium=footer-button&utm_campaign=DWTS',
        },
        {
            id: 'sport',
            url: 'https://7plus.com.au/sport?utm_source=7NEWS&utm_medium=bottom-7plus-button&utm_campaign=sport-article',
        },
        {
            id: 'sunrise',
            url: 'https://7plus.com.au/sunrise?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=sunrise-articles',
        },
        {
            id: 'entertainment',
            url: 'https://7plus.com.au/shows?utm_source=7NEWS&utm_medium=stream-free-bottom-article-button&utm_campaign=entertainment-articles',
        },
        {
            id: 'the-morning-show',
            url: 'https://7plus.com.au/the-morning-show?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=the-morning-show-articles',
        },
        {
            id: 'politics',
            url: 'https://7plus.com.au/news?utm_source=7NEWS&utm_medium=bottom-7plus-button-article&utm_campaign=politics-article',
        },
        {
            id: 'technology',
            url: 'https://7plus.com.au/news?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=tech-articles',
        },
        {
            id: 'news/world',
            url: 'https://7plus.com.au/news?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=world-articles',
        },
        {
            id: 'news',
            url: 'https://7plus.com.au/news?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=news-articles',
        },
        {
            id: 'business',
            url: 'https://7plus.com.au/news?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=business-articles',
        },
        {
            id: 'weather',
            url: 'https://7plus.com.au/news?utm_source=7NEWS&utm_medium=end-of-article-button&utm_campaign=weather-articles',
        },
    ]
    const data = includedArticles.find((item) => topic.includes(item.id))
    if (!data) {
        return { isEnabled: false }
    }
    return {
        isEnabled: true,
        buttonLink: data.url,
    }
}
export interface SubTopicData {
    trendingTitle: string
    trendingCuration: string
    promoCardsCuration: string
    //Some topics (eg. Technology) is listing instead of curation
    //Without flag in that situation subtopic page will crash
    isListingTopic?: boolean
}
interface BauhausSubtopic {
    id: string
    feature: Features
    subtopicData: SubTopicData
}

export function getBauhausSubtopicPageData(
    topic: string,
    toggles: FeatureState,
): { isEnabled: false } | { isEnabled: true; data: SubTopicData } {
    const bauhausSubTopic: BauhausSubtopic[] = [
        {
            id: 'lifestyle',
            feature: '7-news-lifestyle-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'The Latest',
                trendingCuration: 'lifestyle',
                promoCardsCuration: '7news-lifestyle-promo-cards',
            },
        },
        {
            id: 'travel',
            feature: '7-news-lifestyle-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'The Latest',
                trendingCuration: 'lifestyle',
                promoCardsCuration: '7news-lifestyle-promo-cards',
            },
        },
        {
            id: 'entertainment',
            feature: '7-news-entertainment-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'entertainment',
                promoCardsCuration: '7news-entertainment-promo-cards',
            },
        },
        {
            id: 'sunrise',
            feature: '7-news-entertainment-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'entertainment',
                promoCardsCuration: '7news-entertainment-promo-cards',
            },
        },
        {
            id: 'the-morning-show',
            feature: '7-news-entertainment-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'entertainment',
                promoCardsCuration: '7news-entertainment-promo-cards',
            },
        },
        {
            id: 'politics',
            feature: '7-news-politics-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'politics',
                promoCardsCuration: '7news-politics-promo-cards',
            },
        },
        {
            id: 'sport',
            feature: '7-news-sport-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Latest in sport',
                trendingCuration: 'sport',
                promoCardsCuration: 'sport-promo-cards',
            },
        },
        {
            id: 'technology',
            feature: '7-news-tech-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'technology',
                promoCardsCuration: '7news-tech-promo-cards',
                isListingTopic: true,
            },
        },
        {
            id: 'weather/',
            feature: '7-news-weather-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'weather',
                promoCardsCuration: '7news-weather-promo-cards',
                isListingTopic: true,
            },
        },
        {
            id: 'business',
            feature: '7-news-business-sub-topic-page',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'business/finance',
                promoCardsCuration: '7news-finance-promo-cards',
                isListingTopic: true,
            },
        },
        {
            id: 'news',
            feature: '7-news-sub-topic-page-display',
            subtopicData: {
                trendingTitle: 'Trending',
                trendingCuration: 'news',
                promoCardsCuration: '7news-home-promo-cards',
                isListingTopic: true,
            },
        },
    ]

    const topicData = bauhausSubTopic.find((item) => topic.includes(item.id))

    if (!topicData) {
        return { isEnabled: false }
    }

    const isBauhausSubtopicFeatureEnabled = isFeatureEnabled(
        toggles,
        topicData.feature,
    )

    return {
        isEnabled: isBauhausSubtopicFeatureEnabled,
        data: topicData.subtopicData,
    }
}
